import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Mail } from "lucide-react";

const FAQ_DATA = [
  {
    question: "How does Crown Ledger recover failed payments?",
    answer: "The moment a payment fails on PayFast, Peach Payments, or Paystack, Crown Ledger classifies the failure reason and activates a recovery sequence automatically. Your customer receives a precisely-timed series of messages via Email, WhatsApp, and SMS — branded to your business. DebiCheck mandates are re-authenticated directly. You receive recovered revenue. We invoice our performance fee."
  },
  {
    question: "What does Crown Ledger cost?",
    answer: "Nothing unless we recover. Crown Ledger charges a single performance fee — a percentage of the revenue we successfully recover. There is no monthly fee, no setup cost, and no contract. If we recover nothing in a billing cycle, you receive no invoice. Founding members lock in a permanently reduced performance rate."
  },
  {
    question: "Which payment processors do you support?",
    answer: "Crown Ledger supports PayFast, Peach Payments, Paystack, and Ozow natively via webhook integration. DebiCheck re-authentication runs through the Stitch Money API. If your processor is not listed, contact us — we are actively expanding our integration library."
  },
  {
    question: "How long does integration take?",
    answer: "Most businesses are live within 15 minutes. Your activation call is a 12-minute session where a Crown Ledger engineer configures your webhook endpoint directly inside your payment processor dashboard. No developer time is required on your side. No code changes."
  },
  {
    question: "Is Crown Ledger POPIA compliant?",
    answer: "Yes. Crown Ledger is built to South Africa's Protection of Personal Information Act requirements. Customer data used in recovery sequences is processed only for the purpose of payment recovery, stored securely, and never shared with third parties. A full data processing agreement is available on request."
  },

{ question: "Are we a debt collection company/agency",
  answer: " No, Crown Ledger is NOT a debt collection agency nor we offer debt review/ counsellling , we are a software company who acts in behalf of merchants and thus are not liable for any debt the needs to be recovered"

},
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(prev => prev === index ? null : index);
  };

  return (
    <>
      <style>{`
        .faq-section {
          background: var(--bg-void);
          padding: 96px 24px;
        }

        .faq-inner {
          max-width: 1040px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.4fr;
          gap: 80px;
          align-items: start;
        }

        .faq-left {
          position: sticky;
          top: 120px;
        }

        .faq-caption {
          font-family: 'Fira Code', monospace;
          font-weight: 600;
          font-size: 10px;
          color: var(--gold-primary);
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-bottom: 16px;
        }

        .faq-heading {
          font-family: 'Cabinet Grotesk', 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 36px;
          color: var(--text-primary);
          letter-spacing: -0.025em;
          line-height: 1.1;
          margin-bottom: 16px;
        }

        .faq-body {
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 400;
          color: var(--text-secondary);
          line-height: 1.72;
          margin-bottom: 32px;
          max-width: 280px;
        }

        .faq-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Fira Code', monospace;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          background: rgba(184,146,42,0.07);
          border: 1px solid rgba(184,146,42,0.20);
          border-radius: 10px;
          padding: 11px 18px;
          text-decoration: none;
          transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
          letter-spacing: 0.02em;
        }

        .faq-cta:hover {
          background: rgba(184,146,42,0.12);
          border-color: rgba(184,146,42,0.38);
          transform: translateY(-1px);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .faq-item {
          border-bottom: 1px solid rgba(255,255,255,0.07);
          overflow: hidden;
        }

        .faq-trigger {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 0;
          cursor: pointer;
          gap: 16px;
          background: transparent;
          border: none;
          width: 100%;
          text-align: left;
          transition: opacity 0.15s;
        }

        .faq-trigger:hover .faq-question {
          color: var(--text-primary);
        }

        .faq-question {
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          font-weight: 500;
          color: var(--text-secondary);
          line-height: 1.4;
          transition: color 0.2s;
        }

        .faq-item.open .faq-question {
          color: var(--text-primary);
        }

        .faq-icon {
          flex-shrink: 0;
          color: var(--text-muted);
          transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), color 0.2s;
        }

        .faq-item.open .faq-icon {
          transform: rotate(45deg);
          color: var(--gold-primary);
        }

        .faq-answer {
          padding: 0 0 20px;
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.72;
          max-width: 520px;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .faq-inner {
            grid-template-columns: 1fr;
            gap: 44px;
          }

          .faq-left {
            position: static;
          }

          .faq-heading {
            font-size: 28px;
          }

          .faq-cta {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 479px) {
          .faq-section {
            padding: 72px 20px;
          }

          .faq-question {
            font-size: 15px;
          }

          .faq-answer {
            font-size: 13px;
          }
        }
      `}</style>

      <section className="faq-section">
        <div className="faq-inner">
          {/* Left Column */}
          <div className="faq-left">
            <div className="faq-caption">SUPPORT</div>
            <h2 className="faq-heading">Frequently Asked Questions.</h2>
            <p className="faq-body">
              Can't find what you're looking for? Reach out directly and we'll respond within one business day.
            </p>
            <a 
              href="mailto:support@crownledger.com" 
              className="faq-cta"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = 'mailto:support@crownledger.com';
              }}
            >
              <Mail size={14} style={{ color: "var(--gold-primary)" }} />
              Contact Us
            </a>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="faq-list">
            {FAQ_DATA.map((item, index) => (
              <div key={index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
                <button 
                  className="faq-trigger"
                  onClick={() => toggle(index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="faq-question">{item.question}</span>
                  <Plus size={16} className="faq-icon" />
                </button>
                
                <AnimatePresence initial={false}>
                  {openIndex === index && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ 
                        duration: 0.28, 
                        ease: [0.22, 1, 0.36, 1]
                      }}
                    >
                      {item.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
